import {
  Button,
  Heading,
  Input,
  LoadingSpinner,
  isSerialNumberValid,
} from '@rabbit/elements/shared-components';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  DTVendable,
  FBD_Holding_Private,
  FBD_HoldingProxy,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  UserUploadedDocument,
} from '@rabbit/data/types';
import {
  CompletedUploadShape,
  DocTypeShapeTypes,
  OliveHoldingRegistrationFormData,
  UploadedFileCategories,
} from '@rabbit/elements/shared-types';
import { toast } from 'react-toastify';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  FileStorageContext,
  getCompletedUploadsOfCategory,
  LIST_COUNTRIES,
  useSendEmail,
} from '@rabbit/bizproc/react';
import { UserContext } from '@rabbit/olive/context/UserContext';
import { OurDateTime } from '@rabbit/utils/ts';
import { t } from 'i18next';
import { AppContext, createDynamicYupSchema } from '@rabbit/app-context';
import { OliveFileUploader } from '@rabbit/olive/components/organisms/upload-wrapper/OliveFileUploader';
import { useMyConsumerPersona } from '@rabbit/data/portal';
import { AdditionalFields, RequiredType } from '@rabbit/helper';
import { getRetailerById } from '@rabbit/sage/utils/helpers.tsx';
import CommonFormBuilder from 'libs/elements/shared-components/src/lib/molecules/CommonFormBuilder/CommonFormBuilder';

//@ts-ignore
const isDemoEnv = import.meta.env.VITE_DEMO;

interface FormValuesShape {
  purchase_time: Date | null;
  purchase_price: {
    amount: number;
    currency: string;
  };
  purchase_location: {
    docid: string;
    country: string;
  } | null;
  store_not_listed: boolean;
  custom_store_name: string;
  purchase_country: string;
  serial_number: string;
  serial_proof: UserUploadedDocument[];
  [key: string]: any;
}

interface RegisterHoldingInformationStepProps {
  onChange: (value: number, holdingCreationResponse: string) => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setProductRegistrationData: React.Dispatch<OliveHoldingRegistrationFormData>;
  product: DTVendable;
  createUserHolding: (
    holdingData: OliveHoldingRegistrationFormData
  ) => Promise<string | Error>;
}

export function RegisterHoldingInformationStep({
  onChange,
  setIsLoading,
  product,
  createUserHolding,
}: RegisterHoldingInformationStepProps) {
  const CFG_CLAIMS_FLOW = t('CFG_CLAIMS_FLOW');
  const { config, tenantInfo } = useContext(AppContext);
  const serialNumberRequired =
    config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_REQUIRED;
  const persona = useMyConsumerPersona();
  const [retailerData, setRetailerData] = useState<any | null>(null);

  const validationSchema = Yup.object().shape({
    purchase_location: Yup.mixed(),
    store_not_listed: Yup.boolean(),
    ...(config?.HOLDINGS?.MANDATORY_FIELDS?.PURCHASE_PRICE
      ? {
          purchase_price: Yup.object({
            amount: Yup.number()
              .min(1, t('message.pleaseEnterValidAmount'))
              .required(t('message.pleaseEnterValidAmount')),
            currency: Yup.string()
              .trim()
              .required(t('message.currencyIsRequired')),
          })
            .required(t('message.pleaseEnterPurchasePrice'))
            .typeError(t('message.pleaseEnterPurchasePrice')),
        }
      : {
          purchase_price: Yup.object({
            amount: Yup.number(),
            currency: Yup.string(),
          }),
        }),
    purchase_time: Yup.date()
      .max(new Date(), 'Please enter a valid date')
      .required('Please select a purchase time')
      .typeError('Please enter a valid date'),
    ...(isDemoEnv ||
      (serialNumberRequired && {
        serial_number: Yup.string()
          .trim()
          .required('Please enter a serial number'),
      })),
    serial_proof: serialNumberRequired
      ? Yup.array().min(1).required('Please upload a serial number proof')
      : Yup.array(),
    ...createDynamicYupSchema(
      config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS,
      RequiredType.REQUIRED_FOR_CONSUMER
    ).fields,
  });

  const [isSubmiting, setSubmiting] = useState(false);
  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);
  const [serialNumberRanges, setSerialNumberRanges] = useState<
    Array<{ start: string; end: string }>
  >([]);

  const { uploadQueueState, moveCompletedUploadsToAttached } =
    useContext(FileStorageContext) || {};
  const { completed } = uploadQueueState ?? {};
  const serialNumberProofUploads: CompletedUploadShape[] =
    getCompletedUploadsOfCategory(
      completed ?? [],
      UploadedFileCategories.SerialNumberProof
    );
  const serialNumber = window.localStorage.getItem('LRP_VendableSn') ?? '';
  //const formikRef = useRef<FormikProps<FormValuesShape> | null>(null);
  const additionalValues: { [key: string]: any } = {};

  if (config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS) {
    const additionalFields: AdditionalFields =
      config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS;
    for (const key in config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS) {
      if (Object.prototype.hasOwnProperty.call(additionalFields, key)) {
        const element = additionalFields[key];
        additionalValues[key] = element.initialValue;
      }
    }
  }

  const initialValues: FormValuesShape = {
    purchase_time: null,
    purchase_price: {
      amount: 0,
      currency: tenantInfo?.currency || 'GBP',
    },
    purchase_location: null,
    serial_number: serialNumber,
    store_not_listed: false,
    custom_store_name: '',
    purchase_country: '',
    serial_proof: [],
    ...additionalValues,
  };

  const retailer = `${
    PersonaTypeSingleLetter.Retailer
  }${PersonaIdTypeSplitter}${t('tenantLink')}`;
  useEffect(() => {
    (async () => {
      if (retailer) {
        const retailerFetchData = await getRetailerById(retailer);
        if (retailerFetchData) {
          setRetailerData({
            id: retailerFetchData.docid,
            label: retailerFetchData.name,
            value: retailerFetchData.docid,
            country:
              retailerFetchData.addresses &&
              retailerFetchData.addresses.length > 0
                ? retailerFetchData.addresses[0].country
                : 'GB',
          });
        }
      }
    })();
  }, [retailer]);

  // Store serial numbers and ranges for validation
  useEffect(() => {
    if (product && config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED) {
      setSerialNumbers(product.serialNumbers || []);
      setSerialNumberRanges(product.serialNumberRanges || []);
    }
  }, [product, config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED]);

  const holding_private = FBD_Holding_Private.empty();
  const holdingId = config?.CLAIMS.CLAIMS_FLOW.SERIAL_NUMBER_PROOF
    ? FBD_Holding_Private.keygen(holding_private, FBD_Holding_Private)
    : '';

  // Serial number verification happens on form submission

  const onSubmit = async (values: FormValuesShape) => {
    // Check if serial number verification is required
    if (
      config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED &&
      product.serialNumberEnabled
    ) {
      const serialNumber = values.serial_number;

      if (!serialNumber) {
        toast.error(t('general.pleaseEnterSerialNumber'));
        return;
      }

      const isValid = isSerialNumberValid(
        serialNumber,
        serialNumbers,
        serialNumberRanges
      );

      if (!isValid) {
        toast.error(t('message.serialNumberNotFound'));
        return;
      }

      try {
        const mfr = `${
          PersonaTypeSingleLetter.Manufacturer
        }${PersonaIdTypeSplitter}${t('tenantLink')}`;
        // Check if the serial number is already used in any holding proxy
        const query = FBD_HoldingProxy.query()
          .where('mfr', '==', mfr)
          .where('serial', '==', serialNumber);
        const existingHoldings = await query.getContents();

        if (existingHoldings && existingHoldings.length > 0) {
          toast.error(t('general.serialNumberAlreadyUsed'));
          return;
        }
      } catch (error) {
        console.error('Error checking serial number:', error);
        toast.error(t('general.somethingWentWrongPleaseTryAgain'));
        return;
      }
    }

    setSubmiting(true);
    setIsLoading(true);

    // If values include a retailer from the database, then purchase_location_other should be cleared - and vice versa
    const newPurchaseLocation =
      values.custom_store_name && values.store_not_listed
        ? null
        : values.purchase_location?.docid ?? '';

    const newPurchaseLocationOther = values.store_not_listed
      ? values.custom_store_name ?? ''
      : null;

    let additionalData: Record<string, any> = {};

    if (config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS) {
      const additionalFields: AdditionalFields =
        config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS;

      // Ensure additionalData exists in values
      additionalData = {};
      for (const key in additionalFields) {
        if (
          Object.prototype.hasOwnProperty.call(
            values as Record<string, any>,
            key
          )
        ) {
          additionalData[key] = values?.[key] ?? null; // Use null as fallback if missing
        }
      }
    }

    const data = {
      purchase_location: newPurchaseLocation,
      purchase_location_other: newPurchaseLocationOther,
      purchase_price: {
        amount: Number(values.purchase_price.amount),
        currency: values.purchase_price.currency,
      },
      purchase_time: values.purchase_time
        ? OurDateTime.dateToTimestamp(values.purchase_time)
        : 0,
      serial: values?.serial_number ?? '',
      purchase_country: values.purchase_location
        ? values.purchase_location.country
        : values.purchase_country,
      serial_proof: values.serial_proof || [],
      additionalData,
    };

    try {
      const res = await createUserHolding(data);
      const serialNumberProofFiles = serialNumberProofUploads?.flatMap((file) =>
        file.category === UploadedFileCategories.SerialNumberProof &&
        file?.uploadedFile
          ? [file.uploadedFile]
          : []
      );
      if (res) {
        if (
          moveCompletedUploadsToAttached &&
          serialNumberProofFiles &&
          serialNumberProofFiles.length > 0
        ) {
          moveCompletedUploadsToAttached(serialNumberProofUploads);
        }
      }
      if (window.localStorage.getItem('LRP_VendableSn')) {
        window.localStorage.removeItem('LRP_VendableSn');
      }
      toast.success(
        'Product registered successfully! Please upload your receipt.'
      );
      setIsLoading(false);
      if (typeof res === 'string') onChange(2, res);
    } catch (err) {
      toast.error('Something went wrong, please try again.');
      setSubmiting(false);
      setIsLoading(false);
      console.log(err);
    }
  };

  if (!CFG_CLAIMS_FLOW) return <LoadingSpinner size="sm" />;

  return (
    <div className="flex flex-col gap-4">
      <Heading kind={'h3'}>When and where did you buy your product?</Heading>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={false}
        validateOnBlur={false}
        validateOnChange={true}
        onSubmit={onSubmit}
        //innerRef={formikRef}
      >
        {({ errors, values }) => (
          <Form className="font-nunito flex flex-col gap-4">
            <Input
              type="datepicker"
              name="purchase_time"
              settings={{
                placeholder: 'Purchase date',
                maxDate: new Date(),
                hint: '*required',
              }}
            />
            <Input
              type="currency"
              name="purchase_price"
              settings={{
                id: 'purchase_price',
                placeholder: 'Purchase amount',
                currency:
                  values?.purchase_price?.currency ??
                  tenantInfo?.currency ??
                  'GBP',
                hint: config?.HOLDINGS?.MANDATORY_FIELDS?.PURCHASE_PRICE
                  ? '*required'
                  : undefined,
              }}
            />
            <Input
              type="autocomplete-location-retailer"
              name="purchase_location"
              settings={{
                placeholder: `Enter ${config.HOLDINGS.NEW_REGISTRATION_FLOW.DEFAULT_STORE_NAME} name & location`,
                isMulti: false,
                disabled: values.store_not_listed,
                options: [],
                ...(config.HOLDINGS.NEW_REGISTRATION_FLOW
                  .STORES_FROM_FIRESTORE_RETAILERS && {
                  tenantLink: t('tenantLink'),
                }),
              }}
            />
            <Input
              type="checkbox"
              name="store_not_listed"
              settings={{
                checkboxLabel: `${config.HOLDINGS.NEW_REGISTRATION_FLOW.DEFAULT_STORE_NAME} is not listed`,
                checkboxLabelStyles: 'text-base text-gray-500',
              }}
            />
            {values.store_not_listed && (
              <>
                <Input
                  type="text"
                  name="custom_store_name"
                  settings={{
                    id: 'custom_store_name',
                    placeholder: `Enter ${config.HOLDINGS.NEW_REGISTRATION_FLOW.DEFAULT_STORE_NAME} name`,
                  }}
                />
                <Input
                  type="select"
                  label=""
                  name="purchase_country"
                  settings={{
                    options: LIST_COUNTRIES,
                    id: 'purchase_country',
                    placeholder: 'The country where the product was purchased',
                  }}
                />
              </>
            )}
            {!config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_HIDE && (
              <div className="relative">
                <Input
                  type="text"
                  name="serial_number"
                  settings={{
                    placeholder: `Serial number ${
                      serialNumberRequired ? '*' : ''
                    }`,
                    hint: serialNumberRequired ? '*required' : undefined,
                    disabled: serialNumber !== '',
                  }}
                />
                {/* Serial number verification happens on form submission */}
              </div>
            )}
            {config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_REQUIRED && (
              <OliveFileUploader
                label={t('Photo of serial number') + '*'}
                identifiers={{
                  category: UploadedFileCategories.SerialNumberProof,
                  docType: {
                    docid: holdingId,
                    type: DocTypeShapeTypes.Holding,
                  },
                  personaId: persona?.personaId ?? '',
                }}
                name="serial_proof"
                shouldAutoUpdateDocs={false}
                currentFiles={values.serial_proof ?? []}
                maxFiles={1}
                accepts={['image/*', '.pdf']}
              />
            )}
            {Object.keys(additionalValues).length > 0 && (
              <CommonFormBuilder
                fields={config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS}
              />
            )}
            <Button
              kind="primary"
              type="submit"
              disabled={
                Object.keys(values).length === 0 ||
                Object.keys(errors).length > 0 ||
                (config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_REQUIRED &&
                  values.serial_proof?.length === 0) ||
                // values?.purchase_time === 0 ||
                // values?.purchase_location?.length === 0 ||
                isSubmiting
              }
              loading={isSubmiting}
            >
              Confirm details
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default RegisterHoldingInformationStep;
